import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BookingConfirmationService, BookingConfirmationStatus } from '@/utils/booking-confirmation-service';
import { CommunicationRecord } from '@/types';

export const useBookingConfirmationStatus = (bookingId: string) => {
  return useQuery({
    queryKey: ['booking-confirmation-status', bookingId],
    queryFn: () => BookingConfirmationService.getConfirmationStatus(bookingId),
    enabled: !!bookingId,
    staleTime: Infinity, // Never consider data stale
    refetchOnWindowFocus: false, // No refetch on window focus
    refetchOnMount: false, // No refetch on mount if data exists
    refetchOnReconnect: false, // No refetch on network reconnect
    refetchInterval: false, // No polling
  });
};

export const useBookingCommunications = (bookingId: string) => {
  return useQuery({
    queryKey: ['booking-communications', bookingId],
    queryFn: () => BookingConfirmationService.getCommunicationHistory(bookingId),
    enabled: !!bookingId,
    staleTime: Infinity, // Never consider data stale
    refetchOnWindowFocus: false, // No refetch on window focus
    refetchOnMount: false, // No refetch on mount if data exists
    refetchOnReconnect: false, // No refetch on network reconnect
    refetchInterval: false, // No polling
  });
};

export const useRetryBookingConfirmation = () => {
  return useMutation({
    mutationFn: (bookingId: string) => BookingConfirmationService.retryBookingConfirmation(bookingId),
    onSuccess: (data, bookingId) => {
      console.log('Booking confirmation retry successful:', data);
    },
    onError: (error) => {
      console.error('Failed to retry booking confirmation:', error);
    },
  });
};

export const useSendBookingReminder = () => {
  return useMutation({
    mutationFn: ({ bookingId, message }: { bookingId: string; message?: string }) => 
      BookingConfirmationService.sendManualReminder(bookingId, message),
    onSuccess: (data) => {
      console.log('Booking reminder sent successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to send booking reminder:', error);
    },
  });
};

export const useGenerateCheckInForm = () => {
  return useMutation({
    mutationFn: ({ bookingId, expirationHours }: { bookingId: string; expirationHours?: number }) =>
      BookingConfirmationService.generateCheckInFormUrl({ bookingId, expirationHours }),
    onSuccess: (data) => {
      console.log('Check-in form URL generated:', data);
    },
    onError: (error) => {
      console.error('Failed to generate check-in form URL:', error);
    },
  });
};

export const useBookingConfirmationActions = (bookingId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retryConfirmation = async (listingId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await BookingConfirmationService.retryBookingConfirmation(bookingId, listingId);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retry confirmation';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (customMessage?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await BookingConfirmationService.sendManualReminder(bookingId, customMessage);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reminder';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const generateCheckInForm = async (expirationHours?: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await BookingConfirmationService.generateCheckInFormUrl({
        bookingId,
        expirationHours
      });
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate check-in form';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    retryConfirmation,
    sendReminder,
    generateCheckInForm,
  };
};

// Hook for tracking confirmation status with real-time updates
export const useBookingConfirmationTracker = (bookingId: string) => {
  // Use React Query for better caching and deduplication
  const statusQuery = useBookingConfirmationStatus(bookingId);
  const communicationsQuery = useBookingCommunications(bookingId);

  const refreshStatus = async () => {
    await Promise.all([
      statusQuery.refetch(),
      communicationsQuery.refetch()
    ]);
  };

  return {
    status: statusQuery.data || null,
    communications: communicationsQuery.data || [],
    loading: statusQuery.isLoading || communicationsQuery.isLoading,
    error: (statusQuery.error as Error)?.message || (communicationsQuery.error as Error)?.message || null,
    refreshStatus,
  };
};